/**
 * The Resource BU mapping (f2.2) -- one table for six legacy copies.
 *
 * Kimble and COSMA name a Resource BU in their own spelling; the forecast
 * needs a canonical PSU, a country and a location. The six workbook copies
 * had drifted apart, and the conflicts were resolved once, in the seeding
 * migration.
 *
 * The filter here narrows a list already in the browser. It is NOT
 * resolution: the server matches a feed value exactly after normalisation,
 * and a substring match is deliberately not something an import can do.
 *
 * `raw_value` is shown but never edited -- it is the key an import resolves
 * on, and changing it would orphan every past import that matched it. A
 * mapping is retired by setting its status to Inactive; it then stops
 * resolving but stays listed, because it still explains a past import.
 */

import { useMemo, useState } from 'react';
import { Lock, Pencil, Plus, Search } from 'lucide-react';

import {
  ApiError,
  type Country,
  type LookupValue,
  type ResourceBuMapping,
} from '@/api/client';
import { useAddResourceBuMapping, useUpdateResourceBuMapping } from '@/api/queries';
import { EmptyCell } from '@/ui/EmptyCell';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, sortRows, type SortState } from '@/ui/tableSort';

import { DialogField, Modal } from './Modal';
import { matchesFilter, problemsByKey } from './referenceForm';
import {
  MAPPING_DEFAULT_SORT,
  mappingSortValue,
  type MappingSortColumn,
} from './referenceSort';

const RAW_VALUE_IMMUTABLE =
  'The exact string the feed carries. It is the key an import resolves on and cannot be changed.';

export function ResourceBuMappingsPanel({
  mappings,
  countries,
  statuses,
}: {
  mappings: readonly ResourceBuMapping[];
  countries: readonly Country[];
  statuses: readonly LookupValue[];
}) {
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ResourceBuMapping | null>(null);
  const [sort, setSort] = useState<SortState<MappingSortColumn>>(MAPPING_DEFAULT_SORT);

  // Filter first, then order: sorting the rows the reader cannot see is work
  // thrown away on every keystroke.
  const shown = useMemo(
    () =>
      sortRows(
        mappings.filter((mapping) => matchesFilter(mapping, filter)),
        sort,
        mappingSortValue,
      ),
    [mappings, filter, sort],
  );
  const toggleSort = (column: MappingSortColumn) =>
    setSort((current) => nextSort(current, column));

  return (
    <section className="card ref-card ref-panel">
      <div className="card-head">
        <div>
          <h3 className="ref-card-title">Resource BU mapping</h3>
          <span className="hint">
            What each Kimble or COSMA Resource BU means, as one canonical PSU.
          </span>
        </div>
        <div className="ref-head-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" /> Add mapping
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="toolbar">
          <div className="search-field">
            <Search aria-hidden="true" />
            <input
              type="search"
              value={filter}
              aria-label="Filter by raw value or canonical PSU"
              placeholder="Filter by raw value or canonical PSU"
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          <span className="hint">
            {shown.length} of {mappings.length}
          </span>
        </div>

        {mappings.length === 0 ? (
          <div className="empty">
            <h3>No mappings yet</h3>
            <p>Add a mapping to let an import resolve a Resource BU.</p>
          </div>
        ) : shown.length === 0 ? (
          <div className="empty">
            <h3>Nothing matches “{filter}”</h3>
            <p className="empty-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setFilter('')}>
                Clear the filter
              </button>
            </p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data ref-table">
              <thead>
                {/* Every column sorts except the last, which holds the row
                    action and has nothing to order by. */}
                <tr>
                  <SortableHeader
                    column="rawValue"
                    label="Raw value"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    column="canonicalPsu"
                    label="Canonical PSU"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    column="country"
                    label="Country"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    column="location"
                    label="Location"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHeader column="status" label="Status" sort={sort} onSort={toggleSort} />
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((mapping) => (
                  <tr key={mapping.id}>
                    <td>
                      <span className="mono ref-code" title={RAW_VALUE_IMMUTABLE}>
                        <Lock aria-hidden="true" />
                        {mapping.raw_value}
                      </span>
                    </td>
                    <td>{mapping.canonical_psu}</td>
                    {/* A placeholder, not a blank: exactly one legacy value
                        names a GFT business line rather than a country, and
                        that is a fact about the data, not a missing field. */}
                    <td>{mapping.country ? mapping.country.name : <EmptyCell />}</td>
                    <td>{mapping.location_name ?? <EmptyCell />}</td>
                    <td>
                      <span
                        className={`badge ${
                          mapping.status.code === 'INACTIVE' ? 'badge-muted' : 'pill-success'
                        }`}
                      >
                        {mapping.status.name}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-icon"
                          title="Edit mapping"
                          aria-label={`Edit ${mapping.raw_value}`}
                          onClick={() => setEditing(mapping)}
                        >
                          <Pencil aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <AddMappingDialog countries={countries} onClose={() => setAdding(false)} />}
      {editing && (
        <EditMappingDialog
          mapping={editing}
          countries={countries}
          statuses={statuses}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function CountrySelect({
  id,
  value,
  countries,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  countries: readonly Country[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">No country</option>
      {countries.map((country) => (
        <option key={country.id} value={country.id}>
          {country.name}
        </option>
      ))}
    </select>
  );
}

function AddMappingDialog({
  countries,
  onClose,
}: {
  countries: readonly Country[];
  onClose: () => void;
}) {
  const add = useAddResourceBuMapping();
  const [rawValue, setRawValue] = useState('');
  const [canonicalPsu, setCanonicalPsu] = useState('');
  const [countryId, setCountryId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const submit = () => {
    if (add.isPending) return;
    setProblems({});
    setMessage(null);
    add.mutate(
      {
        raw_value: rawValue,
        canonical_psu: canonicalPsu,
        country_id: countryId === '' ? null : countryId,
        location_name: locationName.trim() === '' ? null : locationName,
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          if (error instanceof ApiError && error.status < 500) {
            setProblems(problemsByKey(error.errors));
            setMessage(error.errors.some((item) => item.key) ? null : error.message);
            return;
          }
          setMessage(error instanceof Error ? error.message : 'The mapping could not be added.');
        },
      },
    );
  };

  return (
    <Modal
      title="Add a Resource BU mapping"
      subtitle="The mapping is created Active."
      onClose={onClose}
      onSubmit={submit}
      busy={add.isPending}
      submitLabel={add.isPending ? 'Adding…' : 'Add mapping'}
      message={message}
    >
      <DialogField
        label="Raw value"
        htmlFor="mapping-raw"
        problem={problems.raw_value}
        hint="Exactly as the feed spells it. It cannot be changed later."
        span
      >
        <input
          id="mapping-raw"
          type="text"
          className="mono"
          value={rawValue}
          onChange={(event) => setRawValue(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
      <DialogField
        label="Canonical PSU"
        htmlFor="mapping-psu"
        problem={problems.canonical_psu}
      >
        <input
          id="mapping-psu"
          type="text"
          value={canonicalPsu}
          onChange={(event) => setCanonicalPsu(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
      <DialogField label="Country" htmlFor="mapping-country" problem={problems.country_id}>
        <CountrySelect
          id="mapping-country"
          value={countryId}
          countries={countries}
          disabled={add.isPending}
          onChange={setCountryId}
        />
      </DialogField>
      <DialogField
        label="Location"
        htmlFor="mapping-location"
        problem={problems.location_name}
        hint="A display label only; nothing resolves on it."
        span
      >
        <input
          id="mapping-location"
          type="text"
          value={locationName}
          onChange={(event) => setLocationName(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
    </Modal>
  );
}

function EditMappingDialog({
  mapping,
  countries,
  statuses,
  onClose,
}: {
  mapping: ResourceBuMapping;
  countries: readonly Country[];
  statuses: readonly LookupValue[];
  onClose: () => void;
}) {
  const update = useUpdateResourceBuMapping();
  const [canonicalPsu, setCanonicalPsu] = useState(mapping.canonical_psu);
  const [countryId, setCountryId] = useState(mapping.country?.id ?? '');
  const [locationName, setLocationName] = useState(mapping.location_name ?? '');
  const [statusId, setStatusId] = useState(mapping.status.id);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const submit = () => {
    if (update.isPending) return;
    setProblems({});
    setMessage(null);
    update.mutate(
      {
        mappingId: mapping.id,
        change: {
          canonical_psu: canonicalPsu,
          country_id: countryId === '' ? null : countryId,
          location_name: locationName.trim() === '' ? null : locationName,
          status_id: statusId,
        },
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          if (error instanceof ApiError && error.status < 500) {
            setProblems(problemsByKey(error.errors));
            setMessage(error.errors.some((item) => item.key) ? null : error.message);
            return;
          }
          setMessage(error instanceof Error ? error.message : 'The mapping could not be saved.');
        },
      },
    );
  };

  return (
    <Modal
      title="Edit mapping"
      subtitle={mapping.raw_value}
      onClose={onClose}
      onSubmit={submit}
      busy={update.isPending}
      submitLabel={update.isPending ? 'Saving…' : 'Save mapping'}
      message={message}
    >
      <DialogField
        label="Canonical PSU"
        htmlFor="edit-mapping-psu"
        problem={problems.canonical_psu}
      >
        <input
          id="edit-mapping-psu"
          type="text"
          value={canonicalPsu}
          onChange={(event) => setCanonicalPsu(event.target.value)}
          disabled={update.isPending}
        />
      </DialogField>
      <DialogField label="Country" htmlFor="edit-mapping-country" problem={problems.country_id}>
        <CountrySelect
          id="edit-mapping-country"
          value={countryId}
          countries={countries}
          disabled={update.isPending}
          onChange={setCountryId}
        />
      </DialogField>
      <DialogField
        label="Location"
        htmlFor="edit-mapping-location"
        problem={problems.location_name}
      >
        <input
          id="edit-mapping-location"
          type="text"
          value={locationName}
          onChange={(event) => setLocationName(event.target.value)}
          disabled={update.isPending}
        />
      </DialogField>
      <DialogField
        label="Status"
        htmlFor="edit-mapping-status"
        problem={problems.status_id}
        hint="Inactive stops it resolving, but keeps it listed."
      >
        <select
          id="edit-mapping-status"
          value={statusId}
          disabled={update.isPending}
          onChange={(event) => setStatusId(event.target.value)}
        >
          {statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.name}
            </option>
          ))}
        </select>
      </DialogField>
    </Modal>
  );
}
